import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { 
  SessionConfig, 
  SessionInfo, 
  SessionMessage,
  WorkflowStep 
} from '../types';
import * as sessionApi from '../services/sessionApi';

interface SessionUtilizationState {
  // Session configs by step ID
  sessionConfigs: Record<string, SessionConfig>;
  
  // Session info by session key
  sessionInfos: Record<string, SessionInfo>;
  
  // Session messages by session key
  sessionMessages: Record<string, SessionMessage[]>;
  
  // Loading states
  isLoadingConfig: boolean;
  isLoadingHistory: boolean;
  isLoadingInfo: boolean;
  
  // Error states
  configError: string | null;
  historyError: string | null;
  infoError: string | null;

  // Selected session for viewing
  selectedSessionKey: string | null;
  
  // Actions
  setSessionConfig: (stepId: string, config: SessionConfig) => void;
  updateSessionConfig: (stepId: string, config: SessionConfig) => Promise<void>;
  loadSessionHistory: (sessionKey: string) => Promise<void>;
  clearSessionHistory: (sessionKey: string) => Promise<void>;
  loadSessionInfo: (sessionKey: string) => Promise<void>;
  setSelectedSession: (sessionKey: string | null) => void;
  clearErrors: () => void;
  
  // Bulk projects
  clearAllSessions: (workflowId: string) => Promise<void>;
  exportSessionData: (sessionKey: string) => Promise<string>;
  
  // Helper methods
  getSessionConfig: (stepId: string) => SessionConfig | undefined;
  getSessionInfo: (sessionKey: string) => SessionInfo | undefined;
  getSessionMessages: (sessionKey: string) => SessionMessage[] | undefined;
  hasActiveSession: (stepId: string) => boolean;
}

const defaultSessionConfig: SessionConfig = {
  enabled: false,
  mode: 'isolated',
  type: 'conversation',
  ttl_minutes: 60,
  max_messages: 1000
};

const useSessionUtilizationStore = create<SessionUtilizationState>()(
  devtools(
    (set, get) => ({
      sessionConfigs: {},
      sessionInfos: {},
      sessionMessages: {},
      isLoadingConfig: false,
      isLoadingHistory: false,
      isLoadingInfo: false,
      configError: null,
      historyError: null,
      infoError: null,
      selectedSessionKey: null,

      setSessionConfig: (stepId, config) => {
        set((state) => ({
          sessionConfigs: {
            ...state.sessionConfigs,
            [stepId]: config
          }
        }));
      },

      updateSessionConfig: async (stepId, config) => {
        set({ isLoadingConfig: true, configError: null });
        try {
          const updatedStep = await sessionApi.updateStepSessionConfig(stepId, config);
          set((state) => ({
            sessionConfigs: {
              ...state.sessionConfigs,
              [stepId]: config
            },
            isLoadingConfig: false
          }));
        } catch (error) {
          set({ 
            configError: error instanceof Error ? error.message : 'Failed to update session config',
            isLoadingConfig: false 
          });
          throw error;
        }
      },

      loadSessionHistory: async (sessionKey) => {
        set({ isLoadingHistory: true, historyError: null });
        try {
          const messages = await sessionApi.getSessionHistory(sessionKey);
          set((state) => ({
            sessionMessages: {
              ...state.sessionMessages,
              [sessionKey]: messages
            },
            isLoadingHistory: false
          }));
        } catch (error) {
          set({ 
            historyError: error instanceof Error ? error.message : 'Failed to load session history',
            isLoadingHistory: false 
          });
        }
      },

      clearSessionHistory: async (sessionKey) => {
        set({ isLoadingHistory: true, historyError: null });
        try {
          await sessionApi.clearSessionHistory(sessionKey);
          set((state) => ({
            sessionMessages: {
              ...state.sessionMessages,
              [sessionKey]: []
            },
            sessionInfos: {
              ...state.sessionInfos,
              [sessionKey]: {
                ...state.sessionInfos[sessionKey],
                message_count: 0
              }
            },
            isLoadingHistory: false
          }));
        } catch (error) {
          set({ 
            historyError: error instanceof Error ? error.message : 'Failed to clear session history',
            isLoadingHistory: false 
          });
          throw error;
        }
      },

      loadSessionInfo: async (sessionKey) => {
        set({ isLoadingInfo: true, infoError: null });
        try {
          const info = await sessionApi.getSessionInfo(sessionKey);
          set((state) => ({
            sessionInfos: {
              ...state.sessionInfos,
              [sessionKey]: info
            },
            isLoadingInfo: false
          }));
        } catch (error) {
          set({ 
            infoError: error instanceof Error ? error.message : 'Failed to load session info',
            isLoadingInfo: false 
          });
        }
      },

      setSelectedSession: (sessionKey) => {
        set({ selectedSessionKey: sessionKey });
      },

      clearErrors: () => {
        set({ 
          configError: null, 
          historyError: null, 
          infoError: null 
        });
      },

      clearAllSessions: async (workflowId) => {
        set({ isLoadingHistory: true, historyError: null });
        try {
          await sessionApi.clearAllWorkflowSessions(workflowId);
          // Clear local state for all sessions of this workflow
          set((state) => {
            const clearedMessages = { ...state.sessionMessages };
            const clearedInfos = { ...state.sessionInfos };
            
            // Find and clear sessions for this workflow
            Object.keys(clearedMessages).forEach(key => {
              if (key.startsWith(`orch_${workflowId}`)) {
                clearedMessages[key] = [];
                if (clearedInfos[key]) {
                  clearedInfos[key].message_count = 0;
                }
              }
            });
            
            return {
              sessionMessages: clearedMessages,
              sessionInfos: clearedInfos,
              isLoadingHistory: false
            };
          });
        } catch (error) {
          set({ 
            historyError: error instanceof Error ? error.message : 'Failed to clear all sessions',
            isLoadingHistory: false 
          });
          throw error;
        }
      },

      exportSessionData: async (sessionKey) => {
        const messages = get().sessionMessages[sessionKey] || [];
        const info = get().sessionInfos[sessionKey];
        
        const exportData = {
          session_key: sessionKey,
          session_info: info,
          messages: messages,
          exported_at: new Date().toISOString()
        };
        
        return JSON.stringify(exportData, null, 2);
      },

      getSessionConfig: (stepId) => {
        return get().sessionConfigs[stepId];
      },

      getSessionInfo: (sessionKey) => {
        return get().sessionInfos[sessionKey];
      },

      getSessionMessages: (sessionKey) => {
        return get().sessionMessages[sessionKey];
      },

      hasActiveSession: (stepId) => {
        const config = get().sessionConfigs[stepId];
        return config?.enabled === true;
      }
    }),
    {
      name: 'session-utilization-store'
    }
  )
);

export default useSessionUtilizationStore;
export { defaultSessionConfig };